import {
  Client,
  Conversation,
  JSONObject,
  Participant,
  SendMediaOptions,
} from "@twilio/conversations";
import {
  AgentEvent,
  Callback,
  ConversationMetadata,
  ConversationEventType,
  TokenRefreshCallback,
} from "./definitions/types";
import { AgentActions, ConversationEvents } from "./definitions/constants";
import { copy } from "./definitions/copy";

class ConversationService {
  private client: Client | undefined;
  private conversation: Conversation | undefined;
  private chatIdentity: string | undefined;
  private onTokenRefresh: TokenRefreshCallback | undefined;
  private listeners: { [key: ConversationEventType]: Callback<any>[] } = {};

  isServiceReady = false;
  conversationMetadata: ConversationMetadata | undefined;

  #initTypingEvents = (conversation: Conversation) => {
    const handleTypingEvent =
      (isTyping: boolean) => (participant: Participant) => {
        if (participant.identity !== this.chatIdentity) {
          this.listeners[ConversationEvents.Typing]?.forEach((listener) =>
            listener(isTyping)
          );
        }
      };

    conversation.on("typingStarted", handleTypingEvent(true));
    conversation.on("typingEnded", handleTypingEvent(false));
  };

  #initMessageEvents = (conversation: Conversation) => {
    const updateMessages = async () => {
      const messages = await conversation.getMessages();
      this.listeners[ConversationEvents.Messages]?.forEach((listener) =>
        listener(messages.items)
      );
    };

    conversation.on("messageAdded", updateMessages);
    conversation.on("messageUpdated", updateMessages);
    conversation.on("messageRemoved", updateMessages);
    updateMessages();
  };

  #initParticipantEvents = (conversation: Conversation) => {
    const handleParticipantEvent =
      (action: AgentEvent["action"]) => async (participant: Participant) => {
        const { name, sid } = this.#getAgentDetails(participant);
        const event = { action, agent: { name, sid } };

        this.listeners[ConversationEvents.Agent]?.forEach((listener) =>
          listener(event)
        );
      };

    conversation.on(
      "participantLeft",
      handleParticipantEvent(AgentActions.Leave)
    );
    conversation.on(
      "participantJoined",
      handleParticipantEvent(AgentActions.Join)
    );
  };

  // Init all event listeners from SDK
  #initConversation = async (conversation: Conversation) => {
    this.#initTypingEvents(conversation);
    this.#initMessageEvents(conversation);
    this.#initParticipantEvents(conversation);
  };

  #getAgentDetails = (participant: Participant) => {
    const name = participant.attributes
      ? ((participant.attributes as JSONObject).friendlyName as string)
      : participant.identity || "";
    const sid = participant.sid;

    return { name, sid };
  };

  #handleTokenError = (message: string) => {
    console.error(message);
    this.#setError(copy.errors.authToken.refreshFailed);
  };

  #refreshAuthToken = async () => {
    if (!this.onTokenRefresh) {
      this.#handleTokenError(copy.errors.authToken.refreshCallbackNotSet);
      return;
    }

    if (!this.chatIdentity) {
      this.#handleTokenError(copy.errors.authToken.identityNotFound);
      return;
    }

    const authToken = await this.onTokenRefresh(this.chatIdentity);

    if (!authToken) {
      this.#handleTokenError(copy.errors.authToken.refreshFailed);
      return;
    }

    await this.client?.updateToken(authToken);
  };

  #setError = (errorMessage: string) => {
    errorMessage && console.error(errorMessage);
    this.listeners[ConversationEvents.Error]?.forEach((listener) =>
      listener(errorMessage)
    );
  };

  initService = ({ authToken, liveChatId, identity }) => {
    if (!authToken || authToken.length < 32) {
      // throw new Error("Invalid token");
      this.#setError(copy.errors.authToken.invalidToken);
      return false;
    }

    if (!liveChatId || liveChatId.length < 32) {
      // throw new Error("Invalid conversation SID");
      this.#setError(copy.errors.invalidConversationSid);
      return false;
    }

    this.chatIdentity = identity;
    const client = new Client(authToken);

    client.on("initialized", async () => {
      this.#setError("");

      try {
        // Setup Conversation
        this.conversation = await client.getConversationBySid(liveChatId);
        await this.#initConversation(this.conversation);

        // Add token listeners only if the conversation init is OK
        client.on("tokenAboutToExpire", this.#refreshAuthToken);

        // TODO: Ask for a new token for the same conversation... is it possible?
        client.on("tokenExpired", this.#refreshAuthToken);

        this.isServiceReady = true;

        // Fetch participant by identity
        const participant = await this.conversation.getParticipantByIdentity(
          identity
        );

        this.conversationMetadata = {
          dateCreated: this.conversation.dateCreated,
          createdBy: participant?.sid || null,
        };

        this.listeners[ConversationEvents.Ready]?.forEach((listener) =>
          listener({
            isServiceReady: this.isServiceReady,
            conversationMetadata: this.conversationMetadata,
          })
        );
      } catch (error: any) {
        console.error("Fail on Conversation SDK", error.message);
        this.#setError(copy.errors.conversationNotFound);
      }
    });

    // To catch client initialization errors, subscribe to the `'initFailed'` event.
    client.on("initFailed", (error) => {
      console.error("Fail on client SDK", error);
      this.#setError(copy.errors.authToken.invalidToken);
    });

    // TODO: Should we manage this changes?
    client.on("connectionStateChanged", (state) => {
      console.log("Connection state changed:", state);
    });
    this.client = client;

    return true;
  };

  // Retrieves the chat phase from the conversation attributes.
  getChatPhase = async (): Promise<string | null> => {
    if (this.conversation) {
      const attributesRaw = await this.conversation.getAttributes();
      if (attributesRaw) {
        const attributes = JSON.parse(attributesRaw.toString());
        return attributes.chatPhase || null;
      }
    }

    return null;
  };

  // Public way to send messages to conversations SDK
  sendMessage = async (messageBody: string | FormData | SendMediaOptions) => {
    // console.log("Sending message", messageBody, conversation);
    this.conversation?.sendMessage(messageBody);
  };

  setRefeshTokenCallback = (callback: TokenRefreshCallback) => {
    this.onTokenRefresh = callback;
  };

  addEventListener = <ConversationEvent>(
    eventType: ConversationEventType,
    listener: Callback<ConversationEvent>
  ) => {
    if (!this.listeners[eventType]) {
      this.listeners[eventType] = [];
    }
    // console.log("registered listener", eventType, listener.name);
    this.listeners[eventType].push(listener);
  };

  removeEventListener = <ConversationEvent>(
    eventType: ConversationEventType,
    listener: Callback<ConversationEvent>
  ) => {
    if (this.listeners[eventType]) {
      const index = this.listeners[eventType].indexOf(listener);
      if (index > -1) {
        // console.log("removed listener", eventType, listener.name);
        this.listeners[eventType].splice(index, 1);
      }
    }
  };

  startTyping = () => {
    this.conversation?.typing();
  };
}

export default new ConversationService();
